import React, { useState, useCallback, useEffect } from "react"
import API from "../../utils/API"
import Sweetalert from "../../utils/Sweetalert"

const DEVICE_STATE_LABELS = {
  0: { className: "badge-success", label: "Connecté" },
  1: { className: "badge-warning", label: "Déconnecté" },
  2: { className: "badge-danger", label: "Authentification échouée" },
}

const DEFAULT_DEVICE_ID = 1

function buildApiPath(totemId, subPath = "") {
  return `/admin/totem/${totemId}/ai-targeting-module/remote-devices${subPath}`
}

function DiscoveryModal({ totemId, onClose, onDeviceAdded }) {
  const [step, setStep] = useState("discovering")
  const [devices, setDevices] = useState([])
  const [error, setError] = useState(null)
  const [selectedIdx, setSelectedIdx] = useState(null)

  const [username, setUsername] = useState("admin")
  const [password, setPassword] = useState("123456")
  const [showPassword, setShowPassword] = useState(false)
  const [deviceName, setDeviceName] = useState("")
  const [adding, setAdding] = useState(false)
  const [addStep, setAddStep] = useState("")

  useEffect(() => {
    API.POST(
      buildApiPath(totemId, "/discover"),
      { protoType: ["onvif"] },
      (response) => {
        const data = response.data
        const list = data?.list ?? data ?? []
        setDevices(Array.isArray(list) ? list : [])
        setStep("results")
      },
      (errorMessage) => {
        setError(errorMessage)
        setStep("error")
      },
    )
  }, [totemId])

  const handleAdd = useCallback(() => {
    if (selectedIdx == null) return
    const device = devices[selectedIdx]
    const port = device.proto_info?.proto_port ?? 80

    setAdding(true)
    setAddStep("Récupération de l'URL RTSP...")

    API.POST(
      buildApiPath(totemId, "/rtsp-url"),
      { ip: device.ip, port, username, password },
      (rtspResponse) => {
        const urls = rtspResponse.data
        const rtspUrl = Array.isArray(urls) ? urls[0] : urls?.url ?? urls
        if (!rtspUrl) {
          Sweetalert.fireErrorToast("Aucune URL RTSP trouvée pour cette caméra.")
          setAdding(false)
          setAddStep("")
          return
        }

        setAddStep("Ajout de la caméra...")

        API.POST(
          buildApiPath(totemId),
          {
            deviceId: DEFAULT_DEVICE_ID,
            deviceName: deviceName || device.device_name || "remote-camera",
            rtspUrl,
            username,
            password,
            transportType: "TCP",
          },
          () => {
            setAdding(false)
            Sweetalert.fireSuccessToast()
            onDeviceAdded()
          },
          (errorMessage) => {
            setAdding(false)
            setAddStep("")
            Sweetalert.fireErrorToast(errorMessage)
          },
        )
      },
      (errorMessage) => {
        setAdding(false)
        setAddStep("")
        Sweetalert.fireErrorToast(errorMessage)
      },
    )
  }, [totemId, devices, selectedIdx, username, password, deviceName, onDeviceAdded])

  const selectedDevice = selectedIdx != null ? devices[selectedIdx] : null

  return (
    <>
      <div className="modal fade show" style={{ display: "block" }} tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="fas fa-search mr-2"></i>
                Découverte de caméras distantes
              </h5>
              <button type="button" className="close" onClick={onClose} disabled={adding}>
                <span>&times;</span>
              </button>
            </div>

            <div className="modal-body">
              {step === "discovering" && (
                <div className="text-center py-4">
                  <div className="spinner-border text-info" role="status">
                    <span className="sr-only">Chargement...</span>
                  </div>
                  <p className="mt-2 text-muted">
                    Recherche de caméras sur le réseau...
                    <br />
                    <small>Cette opération peut prendre quelques secondes.</small>
                  </p>
                </div>
              )}

              {step === "error" && (
                <div className="alert alert-danger">
                  <i className="fas fa-exclamation-triangle mr-1"></i>
                  {error}
                </div>
              )}

              {step === "results" && (
                <>
                  {devices.length === 0 ? (
                    <div className="alert alert-info">
                      <i className="fas fa-info-circle mr-1"></i>
                      Aucune caméra ONVIF détectée sur le réseau.
                    </div>
                  ) : (
                    <>
                      <p className="text-muted mb-2">
                        <i className="fas fa-video mr-1"></i>
                        {devices.length} caméra(s) détectée(s). Sélectionnez celle à ajouter.
                      </p>

                      <div className="table-responsive mb-3">
                        <table className="table table-sm table-bordered table-hover mb-0">
                          <thead>
                            <tr>
                              <th style={{ width: "40px" }}></th>
                              <th>Modèle</th>
                              <th>IP</th>
                              <th>MAC</th>
                              <th>Port</th>
                              <th>Protocole</th>
                            </tr>
                          </thead>
                          <tbody>
                            {devices.map((dev, idx) => (
                              <tr
                                key={idx}
                                onClick={() => {
                                  setSelectedIdx(idx)
                                  setDeviceName(dev.device_name || "")
                                }}
                                style={{ cursor: "pointer" }}
                                className={selectedIdx === idx ? "table-primary" : ""}
                              >
                                <td className="text-center">
                                  <input
                                    type="radio"
                                    checked={selectedIdx === idx}
                                    onChange={() => {
                                      setSelectedIdx(idx)
                                      setDeviceName(dev.device_name || "")
                                    }}
                                  />
                                </td>
                                <td>{dev.model || dev.device_name || "-"}</td>
                                <td>{dev.ip || "-"}</td>
                                <td><small>{dev.mac || "-"}</small></td>
                                <td>{dev.proto_info?.proto_port ?? "-"}</td>
                                <td>
                                  <span className="badge badge-info">
                                    {dev.proto_type || "-"}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {selectedDevice && (
                        <div className="border rounded p-3">
                          <h6 className="text-muted text-uppercase mb-3">
                            <i className="fas fa-key mr-1"></i> Identifiants de la caméra
                          </h6>
                          <div className="row">
                            <div className="col-md-4 form-group">
                              <label>Nom (optionnel)</label>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={deviceName}
                                onChange={(e) => setDeviceName(e.target.value)}
                                placeholder="remote-camera"
                                disabled={adding}
                              />
                            </div>
                            <div className="col-md-4 form-group">
                              <label>Identifiant</label>
                              <input
                                type="text"
                                className="form-control form-control-sm"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                disabled={adding}
                              />
                            </div>
                            <div className="col-md-4 form-group">
                              <label>Mot de passe</label>
                              <div className="input-group input-group-sm">
                                <input
                                  type={showPassword ? "text" : "password"}
                                  className="form-control"
                                  value={password}
                                  onChange={(e) => setPassword(e.target.value)}
                                  disabled={adding}
                                />
                                <div className="input-group-append">
                                  <button
                                    type="button"
                                    className="btn btn-outline-secondary"
                                    onClick={() => setShowPassword((p) => !p)}
                                    tabIndex={-1}
                                  >
                                    <i className={`fas fa-eye${showPassword ? "-slash" : ""}`}></i>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {adding && (
                <div className="text-center py-3">
                  <div className="spinner-border spinner-border-sm text-info mr-2" role="status">
                    <span className="sr-only">Chargement...</span>
                  </div>
                  <span className="text-muted">{addStep}</span>
                </div>
              )}
            </div>

            <div className="modal-footer">
              {step === "results" && devices.length > 0 && (
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleAdd}
                  disabled={selectedIdx == null || adding || !username}
                >
                  <i className="fas fa-plus mr-1"></i> Ajouter la caméra
                </button>
              )}
              <button
                type="button"
                className="btn btn-default"
                onClick={onClose}
                disabled={adding}
              >
                {step === "results" ? "Annuler" : "Fermer"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  )
}

export default function RemoteDeviceManager({ totemId, disabled }) {
  const [loading, setLoading] = useState(true)
  const [deviceState, setDeviceState] = useState(null)
  const [deviceConfig, setDeviceConfig] = useState(null)
  const [error, setError] = useState(null)
  const [discoveryOpen, setDiscoveryOpen] = useState(false)
  const [removing, setRemoving] = useState(false)

  const fetchStatus = useCallback(() => {
    setLoading(true)
    setError(null)

    API.POST(
      buildApiPath(totemId, "/state"),
      {},
      (stateResponse) => {
        const stateData = stateResponse.data
        const states = Array.isArray(stateData) ? stateData : stateData?.list ?? []

        if (states.length === 0) {
          setDeviceState(null)
          setDeviceConfig(null)
          setLoading(false)
          return
        }

        setDeviceState(states[0])

        API.POST(
          buildApiPath(totemId, "/config"),
          {},
          (configResponse) => {
            const configData = configResponse.data
            const configs = Array.isArray(configData) ? configData : configData?.list ?? []
            setDeviceConfig(configs[0] ?? null)
            setLoading(false)
          },
          (errorMessage) => {
            setDeviceConfig(null)
            setLoading(false)
            setError(errorMessage)
          },
        )
      },
      (errorMessage) => {
        setError(errorMessage)
        setLoading(false)
      },
    )
  }, [totemId])

  useEffect(() => {
    if (!disabled) {
      fetchStatus()
    }
  }, [disabled, fetchStatus])

  const handleRemove = useCallback(() => {
    Sweetalert.fireDeleteConfirm(
      "Supprimer la caméra distante",
      "La caméra distante sera déconnectée du module IA. Cette action est réversible.",
      () => {
        setRemoving(true)
        API.CUSTOM(
          "delete",
          buildApiPath(totemId),
          { deviceId: deviceConfig?.device_id ?? DEFAULT_DEVICE_ID },
          () => {
            setRemoving(false)
            Sweetalert.fireSuccessToast()
            fetchStatus()
          },
          (err) => {
            setRemoving(false)
            Sweetalert.fireErrorToast(
              err?.response?.data?.message || "Erreur lors de la suppression.",
            )
          },
        )
      },
    )
  }, [totemId, deviceConfig, fetchStatus])

  const handleDeviceAdded = useCallback(() => {
    setDiscoveryOpen(false)
    fetchStatus()
  }, [fetchStatus])

  if (disabled) {
    return (
      <div className="mt-3 pt-3 border-top">
        <h6 className="text-muted">
          <i className="fas fa-video mr-1"></i> Caméra distante
        </h6>
        <p className="text-muted small mb-0">
          <i className="fas fa-info-circle mr-1"></i>
          Le module doit être connecté pour gérer les caméras distantes.
        </p>
      </div>
    )
  }

  const hasDevice = deviceState != null
  const stateBadge = hasDevice
    ? DEVICE_STATE_LABELS[deviceState.state] ?? DEVICE_STATE_LABELS[1]
    : null

  return (
    <div className="mt-3 pt-3 border-top">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h6 className="text-muted mb-0">
          <i className="fas fa-video mr-1"></i> Caméra distante
        </h6>
        {hasDevice && !loading && (
          <button
            className="btn btn-xs btn-outline-info"
            onClick={fetchStatus}
            disabled={removing}
            title="Rafraîchir"
          >
            <i className="fas fa-sync-alt"></i>
          </button>
        )}
      </div>

      {loading && (
        <div className="text-center py-2">
          <div className="spinner-border spinner-border-sm text-info" role="status">
            <span className="sr-only">Chargement...</span>
          </div>
          <small className="text-muted ml-2">Vérification...</small>
        </div>
      )}

      {error && !loading && (
        <div className="alert alert-warning alert-sm py-1 px-2 mb-2" style={{ fontSize: "0.85rem" }}>
          <i className="fas fa-exclamation-triangle mr-1"></i>
          {error}
          <button
            className="btn btn-xs btn-outline-warning ml-2"
            onClick={fetchStatus}
          >
            Réessayer
          </button>
        </div>
      )}

      {!loading && !hasDevice && !error && (
        <div>
          <p className="text-muted small mb-2">
            Aucune caméra distante configurée.
          </p>
          <button
            className="btn btn-outline-success btn-sm"
            onClick={() => setDiscoveryOpen(true)}
          >
            <i className="fas fa-plus mr-1"></i> Ajouter une caméra
          </button>
        </div>
      )}

      {!loading && hasDevice && (
        <div>
          <table className="table table-sm table-bordered mb-2" style={{ tableLayout: "fixed" }}>
            <tbody>
              <tr>
                <th style={{ width: "35%" }}>État</th>
                <td>
                  <span className={`badge ${stateBadge.className}`}>
                    {stateBadge.label}
                  </span>
                </td>
              </tr>
              {deviceConfig && (
                <>
                  <tr>
                    <th>Nom</th>
                    <td>{deviceConfig.device_name || "-"}</td>
                  </tr>
                  <tr>
                    <th>Protocole</th>
                    <td>
                      <span className="badge badge-info">
                        {deviceConfig.proto || "-"}
                      </span>
                    </td>
                  </tr>
                  {deviceConfig.rtsp_param?.url && (
                    <tr>
                      <th>URL RTSP</th>
                      <td>
                        <small style={{ wordBreak: "break-all" }}>
                          {deviceConfig.rtsp_param.url}
                        </small>
                      </td>
                    </tr>
                  )}
                </>
              )}
              {deviceState.channels?.length > 0 && (
                <tr>
                  <th>Flux</th>
                  <td>
                    {deviceState.channels.map((ch, i) => {
                      const streamOk = ch.stream_state === 1
                      return (
                        <span
                          key={i}
                          className={`badge ${streamOk ? "badge-success" : "badge-secondary"} mr-1`}
                        >
                          CH{ch.channel_id ?? i}
                        </span>
                      )
                    })}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <button
            className="btn btn-outline-danger btn-sm"
            onClick={handleRemove}
            disabled={removing}
          >
            {removing ? (
              <>
                <span className="spinner-border spinner-border-sm mr-1" role="status"></span>
                Suppression...
              </>
            ) : (
              <>
                <i className="fas fa-trash-alt mr-1"></i> Supprimer
              </>
            )}
          </button>
        </div>
      )}

      {discoveryOpen && (
        <DiscoveryModal
          totemId={totemId}
          onClose={() => setDiscoveryOpen(false)}
          onDeviceAdded={handleDeviceAdded}
        />
      )}
    </div>
  )
}
